import { t } from '@/i18n';
import { DateRangeSchema, sortOrderType } from '@/utils';
import { z } from 'zod';
import { NAME_MAX_LENGTH } from '../utils';
import { AccountingTypeModel } from './accounting-type';

export const revenueNatureLevelType = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
]);

export const RevenueNatureLevelLabelModifier = {
  1: t('revenue-nature-level-1'),
  2: t('revenue-nature-level-2'),
  3: t('revenue-nature-level-3'),
  4: t('revenue-nature-level-4'),
  5: t('revenue-nature-level-5'),
  6: t('revenue-nature-level-6'),
} as const satisfies Record<z.infer<typeof revenueNatureLevelType>, string>;

export const RevenueNatureBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  code: z.number().int().min(1),
  name: z.string().min(1),
  level: revenueNatureLevelType,
  accountingId: z.string().uuid().nullish(),
  upperId: z.string().uuid().nullable(),
  startYear: z.number().int().nullish(),
  endYear: z.number().int().nullish(),
  budgetAgendaSortOrder: sortOrderType.nullable(),
});

type RecursiveRevenueNature = z.infer<typeof RevenueNatureBaseModel> & {
  upper: RecursiveRevenueNature | null;
};

export const RevenueNaturesModel = RevenueNatureBaseModel.extend({
  accounting: AccountingTypeModel.nullable(),
}).array();

const RecursiveRevenueNatureModel: z.ZodType<RecursiveRevenueNature> =
  RevenueNatureBaseModel.extend({
    upper: z.lazy(() => RecursiveRevenueNatureModel.nullable()),
  });

export const RevenueNatureFormBaseSchema = RevenueNatureBaseModel.pick({
  code: true,
  level: true,
  accountingId: true,
}).extend({
  name: RevenueNatureBaseModel.shape.name.max(NAME_MAX_LENGTH),
  period: DateRangeSchema.nullish(),
  upperIds: RevenueNatureBaseModel.shape.upperId.unwrap().array().nullish(),
});

export const RevenueNatureFormSchema = RevenueNatureFormBaseSchema.refine(
  (data) => !(data.level !== 1 && !data.upperIds?.length),
  {
    path: ['upperIds'],
    message: t('validation.no-upper-selected'),
  },
).refine((data) => data.level !== 1 || data.accountingId, {
  path: ['accountingId'],
  message: t('validation.required'),
});

export const UpdateRevenueNatureParamsSchema =
  RevenueNatureFormBaseSchema.extend({
    id: z.string().uuid(),
  });

export const UpdateRevenueNatureBudgetAgendaSortOrderParamsSchema = z
  .object({
    id: RevenueNatureBaseModel.shape.id,
    budgetAgendaSortOrder: sortOrderType,
  })
  .array();
